/**
 * Shared hook for fetching available AI model categories.
 *
 * Used by ModelCategoryPicker across admin settings, agent processing, and chat.
 * Fetches from the ai-chat available-categories endpoint (accessible to all authenticated users).
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/axiosInstance';

export interface ModelCategory {
  id: string;
  name: string;
  description: string | null;
  tier: string | null;
  provider_id: string;
  provider_name: string;
  resolved_model: string | null;
  available: boolean;
  reasoning_supported: boolean;
  reasoning_level: string | null;
}

interface AvailableCategoriesResponse {
  categories: ModelCategory[];
  by_provider: Record<string, ModelCategory[]>;
}

export const AVAILABLE_CATEGORIES_QUERY_KEY = 'available-model-categories';

export function useAvailableModelCategories() {
  return useQuery({
    queryKey: [AVAILABLE_CATEGORIES_QUERY_KEY],
    queryFn: async (): Promise<ModelCategory[]> => {
      const response = await apiGet<{ data: AvailableCategoriesResponse }>(
        '/api/v1/ai-chat/available-categories'
      );
      return response.data.categories;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
